/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { httpService } from 'src/core/services/api/httpService';
import { luisRequestOptionsBuilder } from 'src/core/utils/api/luisRequestOptionsBuilder';
import { urlBuilder } from 'src/core/utils/api/urlBuilder';
import { createService } from 'src/core/utils/createService';
import { DomainSwagger } from 'src/luis/core/services/domains/domainSwagger';

/**
 * Service that handles the domains API calls.
 */
export const getDomainService = createService(({ context, getEnvironment }) => {
    const { apiUrl, isProduction } = getEnvironment();
    const apiOptions = luisRequestOptionsBuilder()
        .useDefaultOptions(context.serviceKey, isProduction)
        .build();

    /**
     * Gets all prebuilt domains objects.
     */
    const getDomains = async (appCulture: string): Promise<DomainSwagger[]> => {
        const url = urlBuilder()
            .baseUrl(apiUrl)
            .resource('apps')
            .resource('customPrebuiltDomains')
            .resource(appCulture)
            .build();

        const { data: domains } = await httpService.get<DomainSwagger[]>(url, apiOptions);

        return domains;
    };

    /**
     * Issues a request to add the domain to an application.
     */
    const addDomain = async ({ name: domainName, culture }: { name: string; culture: string }): Promise<void> => {
        const url = urlBuilder()
            .baseUrl(apiUrl)
            .resource('apps', context.appId)
            .resource('versions', context.versionId)
            .resource('customPrebuiltDomains')
            .build();

        await httpService.post<string[]>(url, { culture, domainName }, apiOptions);
    };

    /**
     * Issues a request to remove the domain from an application.
     */
    const removeDomain = async ({ name: domainName }: { name: string }): Promise<void> => {
        const url = urlBuilder()
            .baseUrl(apiUrl)
            .resource('apps', context.appId)
            .resource('versions', context.versionId)
            .resource('customPrebuiltDomains')
            .resource(domainName)
            .build();

        await httpService.delete<void>(url, apiOptions);
    };

    return {
        getDomains,
        addDomain,
        removeDomain
    };
});
